/**
 * New Chat Button Component
 * Isolated button for creating new conversations with configuration modal
 */

import React, { useState } from 'react';
import { Button, ActionIcon } from '@mantine/core';
import { IconMessagePlus } from '@tabler/icons-react';
import type { NewChatButtonProps } from '../../types/chat';
import { NewChatModal } from './NewChatModal';

export const NewChatButton = React.memo<NewChatButtonProps>(({
  onClick,
  disabled = false,
  isDesktop,
  models = [],
  characters = [],
  selectedModel = '',
  selectedCharacter = null,
  isLoggedIn = false,
  onNewChatConfirm
}) => {
  const [modalOpened, setModalOpened] = useState(false);

  const handleClick = () => {
    // If we have all required props, show the modal
    if (models.length > 0 && onNewChatConfirm) {
      setModalOpened(true);
    } else {
      // Fallback to direct onClick for backward compatibility
      onClick();
    }
  };

  const handleNewChatConfirm = (modelId: string, characterId: string | null) => {
    if (onNewChatConfirm) {
      onNewChatConfirm(modelId, characterId);
    }
    setModalOpened(false);
  };

  if (isDesktop !== false) {
    return (
      <>
        <Button
          leftSection={<IconMessagePlus size={16} />}
          onClick={handleClick}
          variant="light"
          size="sm"
          disabled={disabled}
        >
          New Chat
        </Button>

        <NewChatModal
          opened={modalOpened}
          onClose={() => setModalOpened(false)}
          selectedModel={selectedModel}
          selectedCharacter={selectedCharacter}
          models={models}
          characters={characters}
          isLoggedIn={isLoggedIn}
          onConfirm={handleNewChatConfirm}
        />
      </>
    );
  }

  return (
    <>
      <ActionIcon
        onClick={handleClick}
        variant="light"
        size="lg"
        disabled={disabled}
        aria-label="New Chat"
      >
        <IconMessagePlus size={16} />
      </ActionIcon>

      <NewChatModal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        selectedModel={selectedModel}
        selectedCharacter={selectedCharacter}
        models={models}
        characters={characters}
        isLoggedIn={isLoggedIn}
        onConfirm={handleNewChatConfirm}
      />
    </>
  );
});

NewChatButton.displayName = 'NewChatButton';